import { useCallback, useMemo } from 'react';

import type { Group } from '@bugbug/core/types/groups';
import type { Step } from '@bugbug/core/types/steps';
import type { Test } from '@bugbug/core/types/tests';
import { useAppDispatch } from '~/modules/store';
import { UIStateActions } from '~/modules/uiState/uiState.redux';
import analytics from '~/services/analytics';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { showGroupError } from '../../tests.utils';
import { useCleanupAfterTestPartialsDelete } from '../useCleanupAfterTestPartialsDelete/useCleanupAfterTestPartialsDelete';

export const useDeleteCurrentTestPartials = () => {
  const dispatch = useAppDispatch();
  const [deleteCurrentTestPartials, deleteCurrentTestPartialsState] =
    testsApi.useDeletePartialsFromTestMutation();
  const cleanUpAfterDelete = useCleanupAfterTestPartialsDelete();

  const handleDeleteCurrentTestPartials = useCallback(
    async ({
      testId,
      stepsIds = [],
      groupsIds = [],
    }: {
      testId: Test['id'];
      stepsIds?: Step['id'][];
      groupsIds?: Group['id'][];
    }) => {
      analytics.trackEvent(
        stepsIds.length === 1 && !groupsIds.length ? 'step_deleted' : 'bulk_steps_deleted',
      );

      const result = deleteCurrentTestPartials({ testId, stepsIds, groupsIds }).unwrap();

      result
        .then((removed) => {
          dispatch(UIStateActions.resetCheckedSteps());
          cleanUpAfterDelete({
            stepsIds: removed.steps,
            groupsIds: removed.groups,
          });
        })
        .catch((error) => {
          showGroupError(ACTIONS.DELETE, error);
        });

      return result;
    },
    [deleteCurrentTestPartials, dispatch, cleanUpAfterDelete],
  );

  return useMemo(
    () => [handleDeleteCurrentTestPartials, deleteCurrentTestPartialsState] as const,
    [handleDeleteCurrentTestPartials, deleteCurrentTestPartialsState],
  );
};
